'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Grid, TextPassage } from '@petsmart-ui/sparky';
import type { FormikErrors, FormikHelpers } from 'formik';
import { Form, Formik } from 'formik';
import Cookies from 'js-cookie';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import type { TreatsLoginLocation } from '@libs/analytics';
import { getAutoshipSubscriptions, LoginEvents, trackError, useLoginEvents } from '@libs/analytics';
import type { IdentityUser } from '@libs/common-next';
import { AccountDrawerType, cartHasVetDietItem, cartHasVetRxItem, clearAutoshipEligibilityBannerImpression, clsx, decodeJwtCookie, getENV, getNextApiRoute, logSentryError, mergeCartNotificationsPreservingValidateOnLoad, PageType, SentryAcpSignInErrors as SentryErrors, trackAuthenticationAttempt, useCart, useCartNotifications, useLocalStorage, useLogout, usePageLayout, useSentryTrackingContext, useUserSession } from '@libs/common-next';
import { hasAutoshipInCart } from '@libs/common-next/utils';
import type { CartUI } from '@libs/common-node';
import { NEW_TAB, PRIMARY_BUTTON_STYLE, SECONDARY_BUTTON_STYLE, TARGET_BLANK, TARGET_SELF } from '@libs/contentful';
import { AUTH_COOKIES, apiFetch, COOKIES, getAccessToken, OG_COOKIE, PROCEED_TO_CHECKOUT_PRESSED, SESSION_EVALUATED_DOM_EVENT } from '@libs/core';
import { type IdentityRouteAuthMergeResponse } from '@libs/experience-api';
import { useBookingEligibility } from '../../hooks/useBookingEligibility';
import { SnackbarUtils, showMergedCartBanner } from '../../utils';
import type { InterstitialCta, InterstitialLinkAction } from '../InterstitialSignIn';
import { KeepSignedInCheckbox } from '../KeepSignedInCheckbox';
import { ValidationAlerts } from '../ValidationAlert';
import { ACTIVATE_OFFER_PAGE, CART_PAGE, CHECKOUT_PAGE, CREATE_ACCOUNT_PAGE, emailExistsMsg, RESET_PASSWORD, SIGN_IN_PAGE, TREATS_REWARDS_AUTHENTICATED_PAGE, TREATS_REWARDS_PAGE } from './constants';
import { EmailField } from './EmailField';
import { ForgotPasswordCta } from './ForgotPasswordCta';
import { getCartLineItemsSignature } from './getCartLineItemsSignature';
import { handleRedirectToServices } from './handleRedirectToServices';
import { PasswordField } from './PasswordField';
import { resolvePostSignInCartMergeNavigation } from './resolvePostSignInCartMergeNavigation';
import { validateSignInForm } from './schema';
import type { SignInFormValues } from './types';
import { notificationsPreservedFromAuth, type ValidateCartMergeResult, validateCartMerge } from './validateCartMerge';
import './SignInForm.scss';
import './SignInFormSoft.scss';

// TODO: try to replace with useRef to prevent re-render.  Had trouble unit testing useRef  Or possibly try window !== undefined
function useIsRendered() {
  const $ = _c(2);
  const [isRendered, setIsRendered] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setIsRendered(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  return isRendered;
}
const CACHED_SOFT_EMAIL_KEY = 'signInFormCachedEmail';
const initialFormState: SignInFormValues = {
  email: '',
  password: '',
  isExistingEmail: false
};
const pagesToRedirect = [TREATS_REWARDS_PAGE, CREATE_ACCOUNT_PAGE, SIGN_IN_PAGE, ACTIVATE_OFFER_PAGE, RESET_PASSWORD];
function normalizeSameOriginUrlToAppPath(urlOrPath: string): string {
  try {
    const parsed = new URL(urlOrPath, globalThis.window.location.origin);
    if (parsed.origin !== globalThis.window.location.origin) {
      return urlOrPath;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return urlOrPath;
  }
}

/** Velocity routes often live under `/[cacheKey]/...`; `usePathname()` is not always root-relative. */
function matchesVelocityRoute(pathname: string, route: string): boolean {
  if (pathname === route) return true;
  if (pathname.endsWith(route)) return true;
  return pathname.includes(`${route}/`);
}
function clearSignInPasswordFromFormikState(formikHelpers: Pick<FormikHelpers<SignInFormValues>, 'setFieldError' | 'setFieldTouched' | 'setFieldValue'>) {
  void formikHelpers.setFieldValue('password', '');
  formikHelpers.setFieldTouched('password', false);
  formikHelpers.setFieldError('password', undefined);
}
export type SignInFormProps = {
  cta?: InterstitialCta;
  linkAction?: InterstitialLinkAction;
  isAutoship?: boolean;
  isInDrawer?: boolean;
  isSoftAuthenticatedServer?: boolean;
  redirectTo?: string;
  location?: TreatsLoginLocation | null;
  pathOverride?: string;
  onSuccessfulSignIn?: () => Promise<void> | void;
};
export function SignInForm({
  cta,
  linkAction,
  isInDrawer = false,
  isSoftAuthenticatedServer,
  location = null,
  pathOverride,
  onSuccessfulSignIn
}: Readonly<SignInFormProps>) {
  const [isFetching, setIsFetching] = useState(false);
  const [authResponse, setAuthResponse] = useState<IdentityRouteAuthMergeResponse | null>(null);
  const [emailExists, setEmailExists] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const isRendered = useIsRendered();
  const sentryContext = useSentryTrackingContext();
  const {
    accountDrawersOpen,
    isAccountDrawerOpen,
    removeAccountDrawers,
    pageType,
    signInDrawerProps,
    setSignInDrawerProps
  } = usePageLayout();
  const {
    setUser,
    userData,
    isSoftAuthenticated: isSoftAuthenticatedFromSession
  } = useUserSession();
  const {
    cart,
    clearCart,
    setCart,
    refetchCart,
    updateSubscriptionStatus,
    enrichCart,
    handleCartError,
    clearErrors,
    clearStockErrors,
    removeLineItemsFromCartState
  } = useCart();
  const logout = useLogout();
  const {
    setNotifications: setCartNotifications
  } = useCartNotifications();
  const [proceedToCheckoutPressed, setProceedToCheckoutPressed] = useLocalStorage<boolean | undefined>(PROCEED_TO_CHECKOUT_PRESSED);
  const {
    fetchCustomerAndPetEligibility
  } = useBookingEligibility();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isSoftAuthenticated = Boolean(isSoftAuthenticatedFromSession || isSoftAuthenticatedServer);
  const redirectUrlParam = searchParams?.get('redirectUrl') ?? null;
  const fromUrlParam = searchParams?.get('from') ?? null;
  const backUrlParam = searchParams?.get('backUrl') ?? null;
  const pathname = usePathname();
  const isCheckoutLogin = pathname?.includes('checkout-login');
  const componentClassname = isSoftAuthenticated ? 'sign-in-form-soft' : 'sign-in-form';
  const formFieldsClassName = isSoftAuthenticated ? `${componentClassname}__fields` : clsx(`${componentClassname}`, isCheckoutLogin && `${componentClassname}--checkout`);
  const idPrefix = useMemo(() => {
    const prefix = isSoftAuthenticated ? 'sign-in-drawer-soft-' : 'sign-in-drawer-';
    return isInDrawer ? prefix : '';
  }, [isInDrawer, isSoftAuthenticated]);
  const {
    trackSuccessSignIn
  } = useLoginEvents();
  const isVetSourceDietEnabled = getENV('NEXT_PUBLIC_VET_SOURCE_DIET_ENABLED');
  const autoshipDiscountUpdatesEnabled = getENV('NEXT_PUBLIC_AUTOSHIP_DISCOUNT_UPDATES_ENABLED');
  useEffect(() => {
    if (!isSoftAuthenticated || isSoftAuthenticated && userData || isSoftAuthenticated && isSoftAuthenticatedServer) {
      setIsLoading(false);
    }
  }, [isSoftAuthenticated, userData, isSoftAuthenticatedServer]);

  // Cache email in sessionStorage to handle component remounts during auth flow
  // During successful authentication, the component may remount causing userData to become undefined temporarily
  // SessionStorage persists across remounts, ensuring the masked email remains visible throughout the auth process
  useEffect(() => {
    if (isSoftAuthenticated && userData?.profile?.emailAddress) {
      const cached = sessionStorage.getItem(CACHED_SOFT_EMAIL_KEY);
      if (userData.profile.emailAddress !== null && userData.profile.emailAddress !== cached) {
        sessionStorage.setItem(CACHED_SOFT_EMAIL_KEY, userData.profile.emailAddress);
      }
    }
  }, [isSoftAuthenticated, userData?.profile?.emailAddress]);
  useEffect(() => {
    if (!pathname || !matchesVelocityRoute(pathname, SIGN_IN_PAGE) || !redirectUrlParam || !backUrlParam) {
      return;
    }
    if (isFetching || authResponse) {
      return;
    }
    if (!getAccessToken()) {
      return;
    }
    router.push(backUrlParam);
  }, [pathname, redirectUrlParam, backUrlParam, isFetching, authResponse, router]);
  function shouldRedirectToCheckout(updatedCart: CartUI) {
    if (!proceedToCheckoutPressed) {
      return false;
    }
    const hasAutoship = hasAutoshipInCart(updatedCart) && !updatedCart.hasAutoshipOrders && autoshipDiscountUpdatesEnabled;
    const hasVetRx = cartHasVetRxItem(updatedCart);
    const hasVetDiet = isVetSourceDietEnabled && cartHasVetDietItem(updatedCart);
    if (hasAutoship || hasVetRx || hasVetDiet) {
      setProceedToCheckoutPressed(undefined);
      return true;
    }
    return false;
  }
  async function handleRedirect(isCartValidationSuccessful: boolean, updatedCart_0?: CartUI | null, guestCartLineItemsSignature?: string | null, drawerRedirectOverride?: string | null) {
    if ((await handleServicesRedirect()) || (await handleSignInPageRedirect()) || handleSignInDrawerRedirect(drawerRedirectOverride) || handleGuestOnlyPageRedirect()) {
      return;
    }
    if (isCartOrCheckout()) {
      await handleCartOrCheckoutRedirect(isCartValidationSuccessful, updatedCart_0, guestCartLineItemsSignature);
    }
    sessionStorage.removeItem(CACHED_SOFT_EMAIL_KEY);
  }
  async function handleServicesRedirect() {
    if (!redirectUrlParam?.includes('https://services')) return false;
    if (redirectUrlParam?.includes('/booking')) {
      const petId = searchParams?.get('petId') ?? null;
      if (!petId) return false;
      const isEligible = await fetchCustomerAndPetEligibility(Number(petId));
      if (!isEligible) return false;
    }
    handleRedirectToServices(searchParams);
    return true;
  }
  function isCartOrCheckout() {
    return pageType === PageType.CART || pageType === PageType.CHECKOUT;
  }
  async function handleCartOrCheckoutRedirect(isCartValidationSuccessful_0: boolean, updatedCart_1?: Nullable<CartUI>, guestCartLineItemsSignature_0?: string | null) {
    if (!isCartValidationSuccessful_0) {
      router.push(CART_PAGE);
      return;
    }
    const decision = resolvePostSignInCartMergeNavigation({
      pageType,
      isCheckoutLogin,
      updatedCart: updatedCart_1,
      guestCartLineItemsSignature: guestCartLineItemsSignature_0,
      shouldRedirectToCheckout
    });
    if (decision.kind === 'clear_proceed_and_stay') {
      setProceedToCheckoutPressed(undefined);
      return;
    }
    if (decision.kind === 'push') {
      if (decision.path === CHECKOUT_PAGE && updatedCart_1?.merged) {
        setCart({
          ...updatedCart_1,
          merged: false
        });
      }
      router.push(decision.path);
    }
  }
  async function handleSignInPageRedirect() {
    if (!pathname || !matchesVelocityRoute(pathname, SIGN_IN_PAGE) || !redirectUrlParam) return false;
    let url = redirectUrlParam;
    const from = fromUrlParam || 'sign-in';
    if (!redirectUrlParam.includes('?from=') && !redirectUrlParam.includes('&from=')) {
      const separator = redirectUrlParam.includes('?') ? '&' : '?';
      url = `${redirectUrlParam}${separator}from=${encodeURIComponent(from)}`;
    }
    router.push(url);
    return true;
  }
  function handleSignInDrawerRedirect(drawerRedirectUrl?: string | null) {
    if (typeof drawerRedirectUrl !== 'string') {
      return false;
    }
    setSignInDrawerProps(null);
    router.push(drawerRedirectUrl);
    return true;
  }
  function handleGuestOnlyPageRedirect() {
    if (pathname && pagesToRedirect.some(page => matchesVelocityRoute(pathname, page))) {
      router.push(TREATS_REWARDS_AUTHENTICATED_PAGE);
      return true;
    }
    return false;
  }
  function handleInvalidSubmit({
    email,
    password
  }: {
    email?: string[];
    password?: string[];
  }) {
    const errorMessage = [...(email ?? []), ...(password ?? [])];
    if (emailExists) {
      errorMessage.push(emailExistsMsg);
    }
    trackError('login', errorMessage.join(' | '));
  }
  function handleApiFail() {
    SnackbarUtils.showPredefined('SERVER_ERROR');
  }
  const isDrawerOpen = accountDrawersOpen?.includes(AccountDrawerType.SIGN_IN);
  const isDrawerFormOpen = isInDrawer && isDrawerOpen;
  const isNonDrawerFormOpen = !isInDrawer && !isAccountDrawerOpen;
  useEffect(() => {
    setAuthResponse(null);
    // Reset cached email when drawer opens for full sign-in — not soft re-auth (cache backs masked email when userData is briefly missing).
    if (isDrawerFormOpen && !isSoftAuthenticated) {
      sessionStorage.removeItem(CACHED_SOFT_EMAIL_KEY);
    }
  }, [isDrawerFormOpen, isNonDrawerFormOpen, isSoftAuthenticated]);
  async function validateMergedCart(mergedCart?: CartUI | null): Promise<ValidateCartMergeResult> {
    return validateCartMerge({
      mergedCart,
      onApiFail: handleApiFail,
      handleCartError,
      setNotifications: setCartNotifications,
      removeLineItemsFromCartState,
      refetchCart,
      clearStockErrors,
      clearErrors,
      enrichCart,
      setCart
    });
  }
  async function applyCartFromMergeAuthResponse(mergeRes: IdentityRouteAuthMergeResponse): Promise<CartUI | null | undefined> {
    const authCart = mergeRes.data?.updatedCart ?? mergeRes.data?.cart;
    const authNotifications = authCart?.notifications;
    let updatedCart_2 = authCart;
    if (!updatedCart_2?.id || !updatedCart_2.version) {
      return updatedCart_2;
    }
    if (autoshipDiscountUpdatesEnabled) {
      const cartWithUpdatedSubscriptionStatus = await updateSubscriptionStatus(updatedCart_2.id, updatedCart_2.version);
      if (cartWithUpdatedSubscriptionStatus?.id) {
        const preservedFromAuth = notificationsPreservedFromAuth(authNotifications, cartWithUpdatedSubscriptionStatus.notifications);
        updatedCart_2 = {
          ...cartWithUpdatedSubscriptionStatus,
          ...(preservedFromAuth.length || authNotifications != null ? {
            notifications: preservedFromAuth
          } : {}),
          merged: updatedCart_2.merged ?? cartWithUpdatedSubscriptionStatus.merged,
          fulfillmentGroups: cartWithUpdatedSubscriptionStatus.fulfillmentGroups?.length > 0 ? cartWithUpdatedSubscriptionStatus.fulfillmentGroups : updatedCart_2.fulfillmentGroups
        };
      }
    }
    const previousCartForEnrichment = updatedCart_2.id === cart?.id ? cart : undefined;
    const enrichedCart = await enrichCart(updatedCart_2, previousCartForEnrichment);
    const preservedNotifications = notificationsPreservedFromAuth(authNotifications, enrichedCart.notifications);
    const cartWithAuthNotifications: CartUI = {
      ...enrichedCart,
      ...(authCart?.merged ? {
        merged: true
      } : {}),
      ...(preservedNotifications.length || authNotifications != null || enrichedCart.notifications != null ? {
        notifications: preservedNotifications
      } : {})
    };
    showMergedCartBanner(Cookies.get(COOKIES.CART_ID), cartWithAuthNotifications);
    Cookies.set(COOKIES.CART_ID, cartWithAuthNotifications.id);
    setCart(cartWithAuthNotifications);
    if (preservedNotifications.length) {
      setCartNotifications(prev => mergeCartNotificationsPreservingValidateOnLoad(prev, preservedNotifications));
    }
    return cartWithAuthNotifications;
  }
  async function processMergeAuthResult(mergeRes_0: IdentityRouteAuthMergeResponse, formikHelpers: FormikHelpers<SignInFormValues>, capturedDrawerRedirectPath: string | null, capturedDrawerOnSuccess?: () => void): Promise<void> {
    if (!('success' in mergeRes_0)) {
      trackAuthenticationAttempt('email_password', {
        success: false,
        failure_reason: 'Authentication failed - no success response',
        error_code: 'NO_SUCCESS_RESPONSE'
      }, sentryContext);
      return;
    }
    setAuthResponse(mergeRes_0);
    setUser(decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN));
    if (!mergeRes_0.success) {
      trackAuthenticationAttempt('email_password', {
        success: false,
        failure_reason: mergeRes_0.message?.header ?? 'Identity API error during sign in',
        error_code: 'UNKNOWN_ERROR'
      }, sentryContext);
      throw new Error(mergeRes_0.message?.header ?? 'Identity API error during sign in');
    }
    const guestCartLineItemsSignatureBeforeMerge = getCartLineItemsSignature(cart ?? null);
    const updatedCart_3 = await applyCartFromMergeAuthResponse(mergeRes_0);
    trackAuthenticationAttempt('email_password', {
      success: true
    }, sentryContext);
    let cartMergeValidation: ValidateCartMergeResult;
    try {
      cartMergeValidation = pageType === PageType.CART || pageType === PageType.CHECKOUT ? await validateMergedCart(updatedCart_3) : {
        success: false,
        cart: updatedCart_3
      };
    } finally {
      clearAutoshipEligibilityBannerImpression();
    }
    clearSignInPasswordFromFormikState(formikHelpers);
    await handleSuccessfulSignIn(cartMergeValidation.success, cartMergeValidation.cart ?? updatedCart_3, mergeRes_0.data?.email, guestCartLineItemsSignatureBeforeMerge, capturedDrawerRedirectPath, capturedDrawerOnSuccess);
  }
  async function handleSubmitForm(values: SignInFormValues, formikHelpers_0: FormikHelpers<SignInFormValues>) {
    if (isFetching) return; // prevent double submit

    // Capture before any await: redirect/onSuccess must not depend on layout state after re-renders or drawer clears.
    const capturedDrawerRedirectPath_0 = typeof signInDrawerProps?.redirectUrl === 'string' ? normalizeSameOriginUrlToAppPath(signInDrawerProps.redirectUrl) : null;
    const capturedDrawerOnSuccess_0 = signInDrawerProps?.onSuccess;

    // prevent multiple sign in from multiple browser tabs
    const accessToken = getAccessToken();
    if (accessToken) {
      const updatedCart_4 = await refetchCart();
      clearSignInPasswordFromFormikState(formikHelpers_0);
      await handleSuccessfulSignIn(false, updatedCart_4, userData?.profile?.emailAddress, undefined, capturedDrawerRedirectPath_0, capturedDrawerOnSuccess_0);
      return;
    }
    setIsFetching(true);
    try {
      const res = await apiFetch<IdentityRouteAuthMergeResponse>(getNextApiRoute(`auth`), {
        method: 'POST',
        body: JSON.stringify({
          email: values.email,
          password: values.password
        }),
        credentials: 'include',
        additionalSuccessCodes: [401]
      });
      if (res == null || res instanceof Error || 'status' in res && res.status === 500) {
        handleApiFail();
        throw new Error('Identity API error during sign in');
      }
      await processMergeAuthResult(res as IdentityRouteAuthMergeResponse, formikHelpers_0, capturedDrawerRedirectPath_0, capturedDrawerOnSuccess_0);
    } catch (err) {
      logSentryError(err, SentryErrors.SignInForm.SubmitFailed);

      // Track authentication failure for caught errors
      trackAuthenticationAttempt('email_password', {
        success: false,
        failure_reason: err instanceof Error ? err.message : 'Unknown error during sign in',
        error_code: 'SIGNIN_FORM_ERROR'
      }, sentryContext);
    } finally {
      setIsFetching(false);
    }
  }
  async function handleSuccessfulSignIn(isCartMergeSuccessful: boolean, updatedCart_5?: CartUI | null, email_0?: string, guestCartLineItemsSignature_1?: string | null, capturedDrawerRedirectPath_1: string | null = null, capturedDrawerOnSuccess_1?: () => void) {
    const drawerRedirectOverride_0 = typeof capturedDrawerRedirectPath_1 === 'string' ? capturedDrawerRedirectPath_1 : null;
    const drawerOnSuccess = capturedDrawerOnSuccess_1;
    setUser(decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN));
    window.dispatchEvent(new CustomEvent(SESSION_EVALUATED_DOM_EVENT));
    await onSuccessfulSignIn?.();
    await handleRedirect(isCartMergeSuccessful, updatedCart_5, guestCartLineItemsSignature_1, drawerRedirectOverride_0 ?? undefined);
    removeAccountDrawers([AccountDrawerType.SIGN_IN]);
    const autoshipSubscriptions = await getAutoshipSubscriptions();
    const isFirstAutoshipEligible = autoshipSubscriptions?.count === 0;

    // Remove Order Groove cookie in order to avoid potential issues
    Cookies.remove(OG_COOKIE, {
      path: '/'
    });
    let loginLocation = location;
    if (redirectUrlParam?.includes('checkout')) {
      loginLocation = isSoftAuthenticated ? LoginEvents.REAUTHENTICATE_CHECKOUT : LoginEvents.CHECKOUT;
    }
    drawerOnSuccess?.();
    trackSuccessSignIn(loginLocation, isFirstAutoshipEligible, pathOverride, email_0, () => {});
  }
  function RenderCTA({
    validateForm
  }: {
    validateForm: (values?: unknown) => Promise<FormikErrors<SignInFormValues>>;
  }) {
    const buttonType = isRendered ? 'submit' : 'button';
    const buttonStyle = cta?.fields.buttonStyle.toLowerCase();
    const ctaVariant = buttonStyle === 'primary' ? PRIMARY_BUTTON_STYLE : SECONDARY_BUTTON_STYLE;
    const buttonProps = {
      'data-testid': 'signInCta',
      fullWidth: true,
      hideText: isFetching,
      loading: isFetching,
      text: cta?.fields?.text ?? 'Sign in',
      type: buttonType as typeof buttonType
    };
    return cta ? <a target={cta.fields.behavior === NEW_TAB ? TARGET_BLANK : TARGET_SELF} href={cta?.fields?.link?.fields?.webUrl ?? '#'} rel="noreferrer">
                <Button {...buttonProps} size="lg" variant={ctaVariant} />
            </a> : <Button {...buttonProps} onClick={async () => {
      if (isRendered) {
        const result = await validateForm();
        if (Object.keys(result).length > 0) {
          handleInvalidSubmit(result as {
            [key: string]: string[];
          });
        }
      }
    }} size="lg" variant="primary" />;
  }
  function maskEmail(email_1: string | undefined | null): string {
    if (!email_1) return '';
    return `${email_1.slice(0, 3)}***`;
  }
  function RenderEmailField() {
    // Get email from userData or sessionStorage cache
    const getEmailToDisplay = () => {
      // Try userData first (preferred source)
      if (userData?.profile?.emailAddress) {
        return userData.profile.emailAddress;
      }

      // Fallback to cached email from sessionStorage (survives remounts)
      if (typeof window !== 'undefined') {
        const cached_0 = sessionStorage.getItem(CACHED_SOFT_EMAIL_KEY);
        if (cached_0) {
          return cached_0;
        }
      }
      return undefined;
    };
    return isSoftAuthenticated ? <Grid.Item className={`${componentClassname}__identify`}>
                <TextPassage data-testid="email-tip" className={`${componentClassname}__identify--email`}>
                    {maskEmail(getEmailToDisplay())}
                </TextPassage>
                <TextPassage>Not you?</TextPassage>
                <Button data-testid="sign-out-cta" onClick={() => logout({
        redirectUrl: '/sign-in',
        forceRedirect: true,
        callback: clearCart
      })} text="Sign out" type="button" variant="link" />
            </Grid.Item> : <EmailField uniqueId={`${idPrefix}email`} emailExists={emailExists} setEmailExists={setEmailExists} />;
  }
  if (isLoading) {
    return;
  }
  return <Formik onSubmit={(values_0, formikHelpers_1) => {
    void handleSubmitForm(values_0, formikHelpers_1);
  }} initialValues={initialFormState} validate={values_1 => validateSignInForm(values_1, isSoftAuthenticated)} validateOnBlur>
            {({
      validateForm: validateForm_0
    }) => <Form noValidate>
                    {authResponse?.data?.code === 401 && <ValidationAlerts defaultHeading={authResponse.message?.header} validationErrors={[{
        message: authResponse.message?.body
      }]} />}
                    <Grid gap="md">
                        <Grid.Item className={formFieldsClassName}>
                            <RenderEmailField />
                            <PasswordField data-testid="testingId" uniqueId={`${idPrefix}password`} />
                            <KeepSignedInCheckbox />
                        </Grid.Item>
                        <Grid.Item className={`${componentClassname}__cta`}>
                            <RenderCTA {...{
            validateForm: validateForm_0
          }} />
                        </Grid.Item>

                        <ForgotPasswordCta componentClassname={componentClassname} webUrl={linkAction?.fields?.link?.webUrl} actionText={linkAction?.fields?.actionText} />
                    </Grid>
                </Form>}
        </Formik>;
}